import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Divider,
  CircularProgress, Alert, Chip, Avatar, Stack
} from '@mui/material';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

/* ─────────────────────────────────────────────
   INFO CELL
───────────────────────────────────────────── */
const Info = ({ label, value, xs = 12, md = 4 }) => (
  <Grid item xs={xs} md={md}>
    <Typography variant="caption" color="#0284c7" fontWeight={600} display="block">
      {label}
    </Typography>
    <Typography fontWeight={600} sx={{ wordBreak: 'break-word' }}>
      {value ?? 'N/A'}
    </Typography>
  </Grid>
);

/* ─────────────────────────────────────────────
   SECTION WRAPPER
───────────────────────────────────────────── */
const Section = ({ title, children }) => (
  <>
    <Typography variant="h6" sx={{ mt: 5, mb: 1 }} color="primary">
      {title}
    </Typography>
    <Divider sx={{ mb: 3 }} />
    <Grid container spacing={3}>{children}</Grid>
  </>
);

/* ─────────────────────────────────────────────
   BOOLEAN DISPLAY HELPER
───────────────────────────────────────────── */
const bool = (val) => {
  if (val === true  || val === 'true')  return 'Yes';
  if (val === false || val === 'false') return 'No';
  return 'N/A';
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const EmployeeProfile = () => {
  const { user } = useAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    api.get(`/onboarding/employee/${user.employeeId}`)
      .then(res => setData(res.data))
      .catch(() => setError('Failed to load profile. Please try again.'))
      .finally(() => setLoading(false));
  }, [user.employeeId]);

  if (loading) return <Box textAlign="center" mt={8}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!data)   return <Alert severity="info"  sx={{ m: 3 }}>No profile found. Please complete the onboarding form.</Alert>;

  return (
    <Box sx={{ p: 3, bgcolor: '#f4faff', minHeight: '100vh' }}>

      {/* ══════════════ HEADER CARD ══════════════ */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
          <Avatar
            sx={{
              width: 100, height: 100,
              bgcolor: 'primary.main',
              fontSize: '2.5rem', fontWeight: 800
            }}
          >
            {data.fullName?.charAt(0)?.toUpperCase() ?? '?'}
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight={800}>
              {data.fullName ?? '—'}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {data.personalEmail ?? '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Date of Joining: {data.dateOfJoining ?? '—'}
            </Typography>
            <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap">
              <Chip
                label={data.status ?? 'PENDING'}
                color={
                  data.status === 'APPROVED'  ? 'success' :
                  data.status === 'REJECTED'  ? 'error'   :
                  data.status === 'VERIFIED'  ? 'info'    : 'warning'
                }
                sx={{ fontWeight: 700 }}
              />
              {data.hrRemarks && (
                <Chip
                  label={`Remarks: ${data.hrRemarks}`}
                  variant="outlined"
                  color="default"
                  size="small"
                  sx={{ maxWidth: 320 }}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>

        {/* ══════════════ PERSONAL ══════════════ */}
        <Section title="Personal Details">
          <Info label="Full Name"        value={data.fullName} />
          <Info label="Date of Birth"    value={data.dateOfBirth} />
          <Info label="Date of Joining"  value={data.dateOfJoining} />
          <Info label="Age"              value={data.age} />
          <Info label="Gender"           value={data.gender} />
          <Info label="Blood Group"      value={data.bloodGroup} />
          <Info label="Personal Email"   value={data.personalEmail} />
          <Info label="Contact No"       value={data.currentContactNo} />
          <Info label="Alternate Contact" value={data.alternateContactNo} />
          <Info label="Father Name"      value={data.fatherName} />
          <Info label="Father Mobile"    value={data.fatherMobile} />
          <Info label="Mother Name"      value={data.motherName} />
          <Info label="Mother Mobile"    value={data.motherMobile} />
          <Info label="Married"          value={bool(data.married)} />
          {data.married && <Info label="Spouse Name"   value={data.spouseName} />}
          {data.married && <Info label="Spouse Mobile" value={data.spouseMobile} />}
        </Section>

        {/* ══════════════ IDENTITY ══════════════ */}
        <Section title="Identity Proofs">
          <Info label="Aadhaar Number"        value={data.aadhaarNumber} />
          <Info label="PAN Number"            value={data.panNumber} />
          <Info label="Voter ID"              value={data.voterIdNumber} />
          <Info label="Driving License"       value={data.drivingLicenseNumber} />
          <Info label="Passport Number"       value={data.passportNumber} />
          <Info label="Ration Card Number"    value={data.rationCardNumber} />
        </Section>

        {/* ══════════════ EDUCATION ══════════════ */}
        <Section title="Education">
          <Info label="10th %"                     value={data.tenthPercentage} />
          <Info label="Highest Qualification"      value={data.highestQualification} />
          <Info label="University / Institution"   value={data.universityInstitution} />
          <Info label="Year of Passing"            value={data.yearOfPassing} />
          <Info label="Educational Qualification"  value={data.educationalQualification} />
          <Info label="Technical Qualification"    value={data.technicalQualification} />
        </Section>

        {/* ══════════════ BANK & STATUTORY ══════════════ */}
        <Section title="Bank & Statutory Details">
          <Info label="Bank Name"       value={data.bankName} />
          <Info label="Account Number"  value={data.bankAccountNumber} />
          <Info label="IFSC Code"       value={data.ifscCode} />
          <Info label="MICR Code"       value={data.micrCode} />
          <Info label="Branch Address"  value={data.branchAddress} md={8} />
          <Info label="Account Type"    value={data.accountType} />
          <Info label="PF UAN"          value={data.pfUan} />
          <Info label="ESIC Number"     value={data.esicNumber} />
        </Section>

        {/* ══════════════ ADDRESS ══════════════ */}
        <Section title="Address — Current">
          <Info label="Current Address" value={data.currentAddress} md={8} />
          <Info label="City"            value={data.currentCity} />
          <Info label="State"           value={data.currentState} />
          <Info label="Pincode"         value={data.currentPincode} />
        </Section>

        <Section title="Address — Permanent">
          <Info label="Permanent Address" value={data.permanentAddress} md={8} />
          <Info label="City"             value={data.permanentCity} />
          <Info label="State"            value={data.permanentState} />
          <Info label="Pincode"          value={data.permanentPincode} />
          <Info label="Contact No"       value={data.permanentContactNo} />
        </Section>

        {/* ══════════════ EXPERIENCE ══════════════ */}
        <Section title="Experience">
          <Info label="Fresher"            value={bool(data.fresher)} />
          <Info label="Experience (Yrs)"   value={data.experienceYears} />
          <Info label="Relevant Experience" value={data.relevantExperience} />
          <Info label="Previous Company"   value={data.previousCompany} />
          <Info label="Previous Position"  value={data.previousPosition} />
          <Info label="Employment From"    value={data.employmentFrom} />
          <Info label="Employment Till"    value={data.employmentTill} />
          <Info label="Reason for Leaving" value={data.reasonForLeaving} md={8} />
        </Section>

        {/* ══════════════ DRA ══════════════ */}
        <Section title="DRA Qualification">
          <Info label="DRA Qualified"        value={bool(data.draQualified)} />
          <Info label="DRA Certificate No"   value={data.draCertificateNumber} />
        </Section>

        {/* ══════════════ EMERGENCY CONTACTS ══════════════ */}
        <Section title="Emergency Contacts">
          {/* Contact 1 */}
          <Info label="Contact 1 — Name"     value={data.emergencyContactName} />
          <Info label="Contact 1 — Relation" value={data.emergencyContactRelation} />
          <Info label="Contact 1 — Number"   value={data.emergencyContactNumber} />
          <Info label="Contact 1 — Address"  value={data.emergencyContactAddress} md={12} />

          {/* Contact 2 */}
          <Info label="Contact 2 — Name"     value={data.emergencyContactName2} />
          <Info label="Contact 2 — Relation" value={data.emergencyContactRelation2} />
          <Info label="Contact 2 — Number"   value={data.emergencyContactNumber2} />
          <Info label="Contact 2 — Address"  value={data.emergencyContactAddress2} md={12} />

          {/* Contact 3 */}
          <Info label="Contact 3 — Name"     value={data.emergencyContactName3} />
          <Info label="Contact 3 — Relation" value={data.emergencyContactRelation3} />
          <Info label="Contact 3 — Number"   value={data.emergencyContactNumber3} />
          <Info label="Contact 3 — Address"  value={data.emergencyContactAddress3} md={12} />
        </Section>

        {/* ══════════════ MEDICAL & LEGAL ══════════════ */}
        <Section title="Medical & Legal">
          <Info label="Medical Issues"       value={bool(data.medicalIssues)} />
          <Info label="Medical Details"      value={data.medicalDetails} md={8} />
          <Info label="Criminal Record"      value={bool(data.criminalRecord)} />
          <Info label="Criminal Details"     value={data.criminalDetails} md={8} />
          <Info label="Financial Default"    value={data.financialDefault} />
          <Info label="Relatives in Company" value={bool(data.relativesInCompany)} />
        </Section>

        {/* ══════════════ DECLARATION & HR ══════════════ */}
        <Section title="Declaration & HR">
          <Info label="Declaration Accepted" value={bool(data.declarationAccepted)} />
          <Info label="Declaration Date"     value={data.declarationDate} />
          <Info label="Submitted By"         value={data.submittedBy} />
          <Info label="Submitted At"         value={data.submittedAt ? new Date(data.submittedAt).toLocaleString() : 'N/A'} />
          <Info label="Approved By"          value={data.approvedBy} />
          <Info label="Approved At"          value={data.approvedAt ? new Date(data.approvedAt).toLocaleString() : 'N/A'} />
          <Info label="HR Remarks"           value={data.hrRemarks} md={12} />
        </Section>

      </Paper>
    </Box>
  );
};

export default EmployeeProfile;